/**
 * `$http` rejection classification, and the filter for the ones RUM collects itself.
 *
 * Collection:
 * - A rejection left unhandled on a native promise reaches RUM's own collector.
 * - RUM serialises the response object into `error.message`, where {@link dropHttpRejections} recovers it, keyed on the response shape.
 *
 * Placement: portal-v2 has no test runner, and its browser code may not import `@ringba/core`, so this is the closest workspace where the parsing can carry tests.
 * Drop this module when portal-v2 retires.
 */

import type { EventFilter, TelemetryEvent } from '@ringba/core/telemetry';

/** The `$http` response shape carried by a rejection. */
export interface HttpRejection {
  status?: unknown;
  config?: { method?: string; url?: string };
}

// An HTTP failure below this is the network or the user's own permissions, not a bug: -1 aborted, 4xx auth.
const MIN_REPORTABLE_HTTP_STATUS = 500;

/** What to do with a value reported as an unhandled rejection. */
export type RejectionVerdict =
  /** Not a rejection this module recognises. Report the original value. */
  | { action: 'pass' }
  /** An `$http` failure below the reportable status, or a `$q` rejection that is the app's own control flow. Drop it. */
  | { action: 'drop' }
  /** An `$http` failure worth reporting, under a message that groups identical endpoints. */
  | { action: 'rewrite'; message: string };

/** Decides the fate of an already-parsed rejection; `null` passes. */
export function classifyHttpRejection(rejection: HttpRejection | null): RejectionVerdict {
  if (!rejection) return { action: 'pass' };

  const status = extractStatus(rejection);
  if (!(status >= MIN_REPORTABLE_HTTP_STATUS)) return { action: 'drop' };

  const { method, path } = rejectionSignature(rejection);
  return { action: 'rewrite', message: `HTTP ${status} ${method} ${path}` };
}

/**
 * Drops or regroups `$http` rejections RUM serialised into `error.message`.
 *
 * Ordering: must run before any filter that rewrites that message, which would destroy the JSON this reads.
 */
export function dropHttpRejections<T extends TelemetryEvent>(): EventFilter<T> {
  return (event) => {
    if (event.type !== 'error' || !event.error?.message) return true;

    const verdict = classifyHttpRejection(parseRejectionFromMessage(event.error.message));
    if (verdict.action === 'drop') return false;
    if (verdict.action === 'rewrite') event.error.message = verdict.message;
    return true;
  };
}

// --- Private ---

// RUM's prefix for a non-Error value is not part of its API, so the payload is located by its first brace.
function parseRejectionFromMessage(message: string): HttpRejection | null {
  const start = message.indexOf('{');
  if (start === -1) return null;

  try {
    return asHttpRejection(JSON.parse(message.slice(start)));
  } catch (_error) {
    return null;
  }
}

/** A numeric `status` beside a `config` is `$http`'s response object, and separates a rejection from any other JSON in a message. */
function asHttpRejection(value: unknown): HttpRejection | null {
  if (value === null || typeof value !== 'object') return null;

  const candidate = value as HttpRejection;
  const hasConfig = candidate.config !== null && typeof candidate.config === 'object';
  return typeof candidate.status === 'number' && hasConfig ? candidate : null;
}

function extractStatus(rejection: HttpRejection): number {
  return typeof rejection.status === 'number' ? rejection.status : NaN;
}

function rejectionSignature(rejection: HttpRejection): { method: string; path: string } {
  const config = rejection.config ?? {};
  const method = (config.method || 'GET').toUpperCase();
  let path = config.url || 'unknown';
  try {
    path = new URL(path).pathname;
  } catch (_error) {
    path = String(path).split('?')[0];
  }
  path = path.replace(/\/RA[0-9a-f]{32}/gi, '/:id');
  return { method, path };
}
